import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useChangelog, type ChangelogEntry } from "../api";

const EVENT_LABEL: Record<ChangelogEntry["event"], string> = {
  request_created: "Proposed",
  approved: "Approved",
  applied_verified: "Applied in Cinch",
  rejected: "Rejected",
  cancelled: "Withdrawn",
};

function describe(e: ChangelogEntry): string {
  if (e.action === "add") return `link ${e.child_name} under ${e.parent_name}`;
  if (e.action === "remove") return `unlink ${e.child_name} from ${e.parent_name}`;
  return `move ${e.child_name} from ${e.parent_name} to ${e.new_parent_name ?? "?"}`;
}

/** Append-only audit trail — every workflow event, newest first. */
export function ChangelogPanel({ companyId }: { companyId?: string }) {
  const q = useChangelog(companyId);

  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading change log…</p>;
  }
  if (q.error) {
    return <p className="text-sm text-destructive">{String(q.error)}</p>;
  }
  const items = q.data?.items ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        <Table containerClassName="max-h-[28rem]">
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Who</TableHead>
              <TableHead>Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody zebra>
            {items.length === 0 && (
              <TableEmpty colSpan={5}>
                Nothing logged yet{companyId ? " for this company" : ""}.
              </TableEmpty>
            )}
            {items.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {new Date(e.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="text-sm font-medium">
                  {EVENT_LABEL[e.event] ?? e.event}
                </TableCell>
                <TableCell className="text-sm">{describe(e)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {e.actor_email ?? "—"}
                </TableCell>
                <TableCell className="max-w-[24rem] truncate text-xs text-muted-foreground">
                  {e.detail ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
